/**
 * Owns the single currently-mounted information block on the respondent's
 * screen (insight D: at most one block is mounted at a time, so this is one
 * value, not a set).
 *
 * The Durable Object is the authority on what is mounted; this hook only
 * reflects the mount/unmount messages it receives. It never sends a client
 * message: on reconnect the DO re-mounts authoritatively, so there is nothing
 * for the client to reconcile or report.
 *
 * @module components/call-screen/hooks/useInformationBlockMount
 */

import { useCallback, useState } from 'react';

/** The currently-mounted information block, or the absence of one. */
export interface MountedInformationBlock {
  /** snake_case field name of the block. */
  fieldName: string;
  /** Relative public serve URL the browser fetches to render the image. */
  imageUrl: string;
  /** Optional headline used as the image's accessible label. */
  title?: string;
  /** Static counterpart used to pause an animation. */
  imageStillUrl?: string;
  /** Direct provider for playback and attribution behavior. */
  imageSource?: 'upload' | 'klipy';
}

/** Optional metadata carried alongside the core mount identity. */
export type MountedInformationBlockMetadata = Omit<
  MountedInformationBlock,
  'fieldName' | 'imageUrl' | 'title'
>;

/** State + reducers for the currently-mounted information block. */
export interface UseInformationBlockMount {
  /** The currently-mounted block, or null when nothing is on screen. */
  block: MountedInformationBlock | null;
  /** Mount (or replace) the on-screen block. */
  mount: (
    fieldName: string,
    imageUrl: string,
    title?: string,
    metadata?: MountedInformationBlockMetadata,
  ) => void;
  /** Unmount the on-screen block when `fieldName` matches the current one. */
  unmount: (fieldName: string) => void;
}

/**
 * Reflect the worker's mount/unmount messages as a single piece of local state.
 */
export function useInformationBlockMount(): UseInformationBlockMount {
  const [block, setBlock] = useState<MountedInformationBlock | null>(null);

  const mount = useCallback((
    fieldName: string,
    imageUrl: string,
    title?: string,
    metadata: MountedInformationBlockMetadata = {},
  ) => {
    setBlock((current) => {
      // A re-mount of the already-mounted field is a visual no-op: keep the same
      // object identity so React does not needlessly re-render the image.
      if (current && current.fieldName === fieldName && current.imageUrl === imageUrl
        && current.title === title
        && current.imageStillUrl === metadata.imageStillUrl
        && current.imageSource === metadata.imageSource) {
        return current;
      }
      return { fieldName, imageUrl, title, ...metadata };
    });
  }, []);

  const unmount = useCallback((fieldName: string) => {
    setBlock((current) => (current && current.fieldName === fieldName ? null : current));
  }, []);

  return { block, mount, unmount };
}
